import * as React from 'react';

import { StatusChip } from '@/design';
import type {
  IInternalFormSchema,
  IInternalSchemaField,
  IInternalValue
} from '../../api/internal-field-contracts';
import { internalFieldsApi } from '../../api/internal-fields-api';
import type { IAdminParticipantHub } from '../../api/participant-contracts';
import { InternalFieldDisplay } from '../../components/field-controls';
import { internalValueText } from '../../internal-field-values';

/**
 * The staff fields read at a glance on the hub's header (the advisor record page's block F2: S2;
 * the F2 plan's R11): Approval status, Quality rating and Travel, each a chip of the field's name
 * and its value verbatim.
 *
 * **`HEADLINE_FIELDS` is a mirror** of survey-api's suffix rule — the one its Quality → Travel
 * write finds Travel by (`<prefix>.quality_rating`, `<prefix>.travel`) — so a survey's field is a
 * headline by the end of its code, whatever the survey's prefix. It moves with survey-api, the
 * `COLUMN_DEFAULTS` precedent. Rejected: a list of whole codes (`fa.travel`, `teams.travel`, …),
 * which a survey added to the catalogue would silently miss.
 *
 * Read off the **opened participant's** record across its surveys — the header is theirs — and
 * only its cycle-less values. A chip needs the schema's `name` (U8: the label as Salesforce shows
 * it), so a value of a field the schema no longer offers draws no chip — Veritant research's
 * "Values of fields no longer offered" draws it — and nothing is drawn until the schema lands or if
 * it fails, which that section says in its own part. The schema read is the page load's shared one
 * (`internalFieldsApi.schema`), so the header costs no read the section does not make. **Two
 * surveys carrying the same suffix** draw a chip each, each naming its survey. Neutral, like the
 * kind's chip: a staff value is not a state of the page.
 */

/** The headline suffixes, in the order drawn. */
export const HEADLINE_FIELDS = ['approval_status', 'quality_rating', 'travel'] as const;

export type HeadlineSuffix = (typeof HEADLINE_FIELDS)[number];

/** The headline a field's code ends in, if any — the suffix rule above, which the compliance
 *  queue's default columns and its pane's Eligibility read too (F9's Task 8). */
export function headlineSuffixOf(code: string): HeadlineSuffix | undefined {
  return HEADLINE_FIELDS.find((suffix) => code.endsWith(`.${suffix}`));
}

interface IHeadline {
  suffix: HeadlineSuffix;
  surveyCode: string;
  value: IInternalValue;
}

/** The opened participant's headline values, by suffix then survey as the record lists them —
 *  the participant's own (`participant_survey`): a field recorded per response holds a value per
 *  response among the cycle-less ones, with nothing to say which, and is not a headline. */
function headlinesOf(hub: IAdminParticipantHub, surveyCode?: string): IHeadline[] {
  const record = hub.internal?.find(
    (entry) => entry.participantId === hub.participant.participantId
  );
  const surveys = (record?.surveys ?? []).filter(
    (survey) => surveyCode === undefined || survey.surveyCode === surveyCode
  );
  const found = surveys.flatMap((survey) =>
    survey.values.flatMap((value) => {
      const suffix = headlineSuffixOf(value.field);
      return suffix && value.scope === 'participant_survey'
        ? [{ suffix, surveyCode: survey.surveyCode, value }]
        : [];
    })
  );
  return HEADLINE_FIELDS.flatMap((suffix) => found.filter((entry) => entry.suffix === suffix));
}

/** The schemas of the given surveys as they land; a failed read stays absent (see above) until
 *  a later read of it lands — a part's "Read again" — which `onSchema` brings here too. */
function useSchemas(surveyCodes: string[]): ReadonlyMap<string, IInternalFormSchema> {
  const [schemas, setSchemas] = React.useState<ReadonlyMap<string, IInternalFormSchema>>(
    () => new Map()
  );
  const key = surveyCodes.join(' ');
  React.useEffect(() => {
    let live = true;
    const codes = key.split(' ').filter(Boolean);
    const take = (code: string, schema: IInternalFormSchema) => {
      if (live && codes.includes(code)) setSchemas((held) => new Map(held).set(code, schema));
    };
    const unsubscribe = internalFieldsApi.onSchema(take);
    for (const code of codes) {
      internalFieldsApi.schema(code).then(
        (schema) => take(code, schema),
        () => undefined
      );
    }
    return () => {
      live = false;
      unsubscribe();
    };
  }, [key]);
  return schemas;
}

function fieldIn(
  schema: IInternalFormSchema | undefined,
  code: string
): IInternalSchemaField | undefined {
  return schema?.groups.flatMap((group) => group.fields).find((field) => field.code === code);
}

/** The header's headline chips, as `li`s of its "At a glance" list — or, on a person's survey tab
 *  (SR-391), that survey's alone, in the tab's strip. */
export function ResearchHeadline({
  hub,
  surveyCode
}: {
  hub: IAdminParticipantHub;
  surveyCode?: string;
}) {
  const headlines = headlinesOf(hub, surveyCode);
  const schemas = useSchemas([...new Set(headlines.map((entry) => entry.surveyCode))]);

  return headlines.map(({ suffix, surveyCode, value }) => {
    const field = fieldIn(schemas.get(surveyCode), value.field);
    if (field === undefined) return null;
    const shared = headlines.filter((entry) => entry.suffix === suffix).length > 1;
    const options = [...value.options, ...field.options];
    return (
      <li key={`${suffix} ${surveyCode}`} data-headline={suffix}>
        <StatusChip wrap>
          {shared ? `${field.name} (${surveyCode}): ` : `${field.name}: `}
          {value.value.t === 'rich_text' ? (
            <InternalFieldDisplay payload={value.value} />
          ) : (
            internalValueText(value.value, options)
          )}
        </StatusChip>
      </li>
    );
  });
}
